import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import ReactCountryFlag from 'react-country-flag';
import { fromUnixTime, isSameDay, eachDayOfInterval, format } from 'date-fns';
import { Panel, CardButton, BrowserIcon, OSIcon, Tooltip } from '@crazyegginc/hatch';

import { RelativeBarChartPanel } from '../components/RelativeBarChartPanel';
import { Chart } from '../components/chart';
import { SkeletonChart } from '/src/components/SkeletonChart';
import { AISummary } from '../components/AISummary';
import { DonutPanel } from '../components/DonutPanel';
import { DeltaPill } from '../components/DeltaPill';
import { GoalSelector } from '../components/goal-selector';

import { useQueryParams, useHasFeatureFlag, useMetric, useMountOnce, usePermissions, useModal } from '/src/hooks';
import { DIMENSIONS, getEmptyValues, WEB_ANALYTIC_METRICS } from '../constants';
import { FEATURES } from '/src/features/_global/constants';
import { clampNumber } from '/src/features/_global/utils';

const EMPTY_VALUES = getEmptyValues();
const MAX_DAYS = 365;

import {
  constantizeString,
  formatBrowserName,
  formatOsName,
  parseDateRange,
  parsePredefinedRange,
  percentizeValue,
} from '../utils';
import { msToHumanDuration } from '/src/utils/date';
import { abbreviateNumber } from '/src/utils';

import { Favicon } from '../components/Favicon';
import { InstantHeatmaps } from '../components/InstantHeatmaps';
import { FirstVisitModal } from '/src/features/web-analytics/components/modals/FirstVisitModal';
import { SOURCE_TYPE, useSelectedSource } from '/src/components/source-selector/source-selector';

export function ListView({ analytics, conversions = [], isLoading = false, lowerBoundary, upperBoundary }) {
  const { selectedSource } = useSelectedSource();
  const { get: paramGet, set: paramSet, remove: paramRemove } = useQueryParams();
  const selectedMetric = paramGet('metric');
  const hasOverTime = useHasFeatureFlag('analytics-over-time');
  const hasGaImport = useHasFeatureFlag('ga-import');
  const trackMetric = useMetric();
  const { pathname } = useLocation();
  const permissions = usePermissions();
  const canViewAiSummary = permissions.can('summarize', FEATURES.AI).allowed;

  const pagesPanelRef = useRef(null);
  const modal = useModal();

  useMountOnce(() => {
    trackMetric('feature_used', {
      feature: 'web_analytics_dashboard',
    });
    trackMetric('web_analytics', {
      action_type: 'Dashboard',
    });
  }, `page:${pathname}`);

  const selectMetric = useCallback(
    (newValue) => {
      if (newValue) {
        paramSet('metric', newValue);
      }
    },
    [paramSet],
  );

  const selectedMetricData = useMemo(() => {
    const metricData = analytics?.[selectedMetric] ?? {};

    if ((metricData?.days ?? []).length === 0) {
      // generate empty values
      const dateRange = paramGet('date_range');
      const parsedDateRange = parseDateRange(dateRange, lowerBoundary, upperBoundary, MAX_DAYS);

      // if parseDateRange returned [null, null, null], avoid invalid intervals
      if (parsedDateRange.every((v) => v === null)) {
        return { days: [], delta: 0, total: 0 };
      }

      return {
        days: generateEmptyRange(parsedDateRange),
        delta: 0,
        total: 0,
      };
    }
    return analytics?.[selectedMetric] ?? {};
  }, [analytics, selectedMetric, paramGet, lowerBoundary, upperBoundary]);

  useEffect(() => {
    if (!selectedMetricData) {
      paramRemove('metric');
    }
  }, [selectedMetricData, paramRemove]);

  useEffect(() => {
    if (isLoading || !selectedSource || selectedSource.type === SOURCE_TYPE.GA_PROPERTY) {
      return;
    }

    let scrollTimeout;

    const hasSeenModal = localStorage.getItem('hasSeenInstantHeatmapsModal');
    const hasSeenGAModal = localStorage.getItem('hasSeenGAModal');

    if (hasGaImport && !hasSeenGAModal) {
      localStorage.setItem('hasSeenGAModal', 'true');

      modal.show(
        <FirstVisitModal
          isGA={true}
          onClose={() => {
            window.dispatchEvent(new CustomEvent('openSourcePicker'));
          }}
        />,
      );
    } else if (!hasGaImport && !hasSeenModal) {
      localStorage.setItem('hasSeenInstantHeatmapsModal', 'true');

      modal.show(
        <FirstVisitModal
          onClose={() => {
            const SCROLL_DELAY = 100;
            scrollTimeout = setTimeout(() => {
              pagesPanelRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              });
            }, SCROLL_DELAY);
          }}
        />,
      );
    }

    return () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [selectedSource, modal, isLoading, hasGaImport]);

  const hasSelectedMetricData = Object.values(selectedMetricData).length > 0;

  return (
    <div className="flex flex-col w-full space-y-5">
      <Panel variant="secondary">
        <div className="flex justify-between w-full">
          <div className={classNames('flex space-x-5 w-full', { 'mb-4': isLoading || hasSelectedMetricData })}>
            <CardButton
              title="Unique visitors"
              active={selectedMetric === WEB_ANALYTIC_METRICS.UNIQUE_VISITORS}
              onClick={() => selectMetric(WEB_ANALYTIC_METRICS.UNIQUE_VISITORS)}
              isLoading={isLoading}
              className="!h-auto !min-w-[180px]"
              data-testid="MetricButton"
              data-active={selectedMetric === WEB_ANALYTIC_METRICS.UNIQUE_VISITORS}
            >
              <div className="flex flex-col">
                <span className="mr-2.5 block truncate text-[30px] font-bold leading-none text-black-pearl-500 mb-1.25 lowercase">
                  {abbreviateNumber(analytics?.uniqueVisitors?.total ?? 0)}
                </span>
                <DeltaPill delta={analytics?.uniqueVisitors?.delta ?? 0} />
              </div>
            </CardButton>
            <CardButton
              title="Pageviews"
              active={selectedMetric === WEB_ANALYTIC_METRICS.PAGE_VIEWS}
              onClick={() => selectMetric(WEB_ANALYTIC_METRICS.PAGE_VIEWS)}
              isLoading={isLoading}
              className="!h-auto !min-w-[142px]"
              data-testid="MetricButton"
              data-active={selectedMetric === WEB_ANALYTIC_METRICS.PAGE_VIEWS}
            >
              <div className="flex flex-col">
                <span className="mr-2.5 block truncate text-[30px] font-bold leading-none text-black-pearl-500 mb-1.25 lowercase">
                  {abbreviateNumber(analytics?.pageViews?.total ?? 0)}
                </span>
                <DeltaPill delta={analytics?.pageViews?.delta ?? 0} />
              </div>
            </CardButton>
            <CardButton
              title="Sessions"
              active={selectedMetric === WEB_ANALYTIC_METRICS.SESSIONS}
              onClick={() => selectMetric(WEB_ANALYTIC_METRICS.SESSIONS)}
              isLoading={isLoading}
              className="!h-auto !min-w-[144px]"
              data-testid="MetricButton"
              data-active={selectedMetric === WEB_ANALYTIC_METRICS.SESSIONS}
            >
              <div className="flex flex-col">
                <span className="mr-2.5 block truncate text-[30px] font-bold leading-none text-black-pearl-500 mb-1.25 lowercase">
                  {abbreviateNumber(analytics?.sessions?.total ?? 0)}
                </span>
                <DeltaPill delta={analytics?.sessions?.delta ?? 0} />
              </div>
            </CardButton>
            <CardButton
              title="Avg. session duration"
              active={selectedMetric === WEB_ANALYTIC_METRICS.AVG_SESSION_DURATION}
              onClick={() => selectMetric(WEB_ANALYTIC_METRICS.AVG_SESSION_DURATION)}
              isLoading={isLoading}
              className="!h-auto !min-w-[154px]"
              data-testid="MetricButton"
              data-active={selectedMetric === WEB_ANALYTIC_METRICS.AVG_SESSION_DURATION}
            >
              <div className="flex flex-col">
                <span className="mr-2.5 block truncate text-[30px] font-bold leading-none text-black-pearl-500 mb-1.25 lowercase">
                  {msToHumanDuration(analytics?.avgSessionDuration?.total ?? 0, false, true)}
                </span>
                <DeltaPill delta={analytics?.avgSessionDuration?.delta ?? 0} />
              </div>
            </CardButton>
            <CardButton
              title="Bounce rate"
              active={selectedMetric === WEB_ANALYTIC_METRICS.BOUNCE_RATE}
              onClick={() => selectMetric(WEB_ANALYTIC_METRICS.BOUNCE_RATE)}
              isLoading={isLoading}
              className="!h-auto !min-w-[163px]"
              data-testid="MetricButton"
              data-active={selectedMetric === WEB_ANALYTIC_METRICS.BOUNCE_RATE}
            >
              <div className="flex flex-col">
                <span className="mr-2.5 block truncate text-[30px] font-bold leading-none text-black-pearl-500 mb-1.25 lowercase">
                  {percentizeValue(clampNumber(analytics?.bounceRate?.total ?? 0, 0, 1))}
                </span>
                <DeltaPill delta={analytics?.bounceRate?.delta ?? 0} positiveColor="red" negativeColor="green" />
              </div>
            </CardButton>
          </div>
        </div>
        <div className="flex w-full items-center justify-between mb-2.5">
          <span>&nbsp;</span>
          {hasOverTime ? (
            <GoalSelector
              onSelect={(_goal) => {
                // TODO: update query params
              }}
            />
          ) : null}
        </div>
        <div className={classNames('w-full', { 'h-[250px]': isLoading || hasSelectedMetricData })}>
          {isLoading ? (
            <SkeletonChart />
          ) : (
            <>
              {hasSelectedMetricData ? (
                <Chart
                  data={selectedMetricData}
                  conversions={conversions}
                  yScaleCallback={getYScaleCallback(selectedMetric)}
                  yScaleStepSize={getYScaleStepSize(selectedMetric)}
                  getMetricName={getMetricName(selectedMetric)}
                  suggestedMax={getSuggestedMax(selectedMetric)}
                  max={getMax(selectedMetric)}
                />
              ) : null}
            </>
          )}
        </div>
      </Panel>

      {canViewAiSummary ? (
        <AISummary analyticsData={analytics} enabled={analytics?.pageViews?.total > 0} loading={isLoading} />
      ) : null}

      <ul className="grid w-full grid-cols-3 grid-rows-auto gap-5">
        <DonutPanel
          title="New vs returning visitors"
          subtitle="Visitors"
          data={getVisitorsDonutData(analytics?.visitors)}
          isLoading={isLoading}
          size="180px"
          skeletonClassName="!w-[180px] !h-[180px]"
          hasDetails={false}
          dimension={'newVsReturning'}
        />
        <RelativeBarChartPanel
          slug="countries"
          title="Countries"
          data={analytics?.countries}
          isLoading={isLoading}
          renderIcon={(row) => {
            return (
              <ReactCountryFlag
                countryCode={row.code}
                svg
                alt={row.name}
                style={{
                  width: '18px',
                  height: '18px',
                  border: '1px solid #e1e6ef',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  overflow: 'hidden',
                  flexShrink: '0',
                }}
              />
            );
          }}
        />
        <RelativeBarChartPanel slug="languages" title="Languages" data={analytics?.languages} isLoading={isLoading} />
        <DonutPanel
          title="Device type"
          subtitle="Devices"
          size="180px"
          skeletonClassName="!w-[140px] !h-[140px]"
          data={getDeviceDonutData(analytics?.deviceType)}
          isLoading={isLoading}
          hasDetails={true}
          dimension={DIMENSIONS.DEVICE_TYPE}
        />
        <RelativeBarChartPanel
          title="OS"
          slug="os"
          subTitle="Visitors"
          data={analytics?.os}
          renderIcon={(row) => (
            <OSIcon className="w-4 h-4" os={constantizeString(row.name)} variant="COLOR" renderUnknown={true} />
          )}
          rowFormatter={(row) => formatOsName(row.name)}
          isLoading={isLoading}
          takeCount={7}
        />
        <RelativeBarChartPanel
          title="Browsers"
          slug="browsers"
          subTitle="Visitors"
          data={analytics?.browsers}
          renderIcon={(row) => (
            <BrowserIcon
              className="w-4 h-4"
              browser={constantizeString(row.name)}
              variant="COLOR"
              renderUnknown={true}
            />
          )}
          rowFormatter={(row) => formatBrowserName(row.name)}
          isLoading={isLoading}
          takeCount={7}
        />
        {analytics?.channels?.data?.length || isLoading ? (
          <RelativeBarChartPanel slug="channels" title="Channels" data={analytics?.channels} isLoading={isLoading} />
        ) : null}
        {analytics?.referrers?.data?.length || isLoading ? (
          <RelativeBarChartPanel
            title="Referrers"
            slug="referrers"
            data={analytics?.referrers}
            isLoading={isLoading}
            renderIcon={(item) => <Favicon domain={item.favicon} />}
            rowFormatter={(item) =>
              item.domain !== item.name ? (
                <Tooltip placement="bottom" tooltipContent={item.domain}>
                  <span>{item.name}</span>
                </Tooltip>
              ) : (
                item.name
              )
            }
          />
        ) : null}
        {analytics?.pages?.data?.length || isLoading ? (
          <RelativeBarChartPanel
            slug="pages"
            title="Pages"
            valueLabel="Pageviews"
            valueKey="pageViews"
            data={analytics?.pages}
            isLoading={isLoading}
            rowFormatter={pageRowFormatter(selectedSource?.name)}
            instantHeatmaps={(item) => (
              <InstantHeatmaps path={item.name} domain={item.domain} selectedSource={selectedSource} />
            )}
          />
        ) : null}
        {analytics?.entryPages?.data?.length || isLoading ? (
          <RelativeBarChartPanel
            ref={pagesPanelRef}
            slug="entryPages"
            title="Entry pages"
            data={analytics?.entryPages}
            isLoading={isLoading}
            rowFormatter={pageRowFormatter(selectedSource?.name)}
            instantHeatmaps={(item) => (
              <InstantHeatmaps path={item.name} domain={item.domain} selectedSource={selectedSource} />
            )}
          />
        ) : null}
        {analytics?.utmCampaigns?.data?.length || isLoading ? (
          <RelativeBarChartPanel
            slug="utmCampaigns"
            title="UTM Campaigns"
            data={analytics?.utmCampaigns}
            isLoading={isLoading}
          />
        ) : null}
        {analytics?.utmSources?.data?.length || isLoading ? (
          <RelativeBarChartPanel
            slug="utmSources"
            title="UTM Source"
            data={analytics?.utmSources}
            isLoading={isLoading}
          />
        ) : null}
        {analytics?.utmMediums?.data?.length || isLoading ? (
          <RelativeBarChartPanel
            slug="utmMediums"
            title="UTM Medium"
            data={analytics?.utmMediums}
            isLoading={isLoading}
          />
        ) : null}
        {analytics?.utmTerms?.data?.length || isLoading ? (
          <RelativeBarChartPanel slug="utmTerms" title="UTM Terms" data={analytics?.utmTerms} isLoading={isLoading} />
        ) : null}
        {analytics?.utmContents?.data?.length || isLoading ? (
          <RelativeBarChartPanel
            slug="utmContents"
            title="UTM Content"
            data={analytics?.utmContents}
            isLoading={isLoading}
          />
        ) : null}
      </ul>
    </div>
  );
}

export function pageRowFormatter(siteName) {
  return (row) => {
    const showDomain =
      (row.domain.length > 0 && row.domain !== siteName) || (row.name.length === 0 && row.domain.length === 0);
    const renderDomain = showDomain ? `${row.domain === '' ? siteName : row.domain}` : '';
    return `${renderDomain}${row.name}`;
  };
}

const getVisitorsDonutData = (input) => {
  return {
    labels: ['New', 'Returning'],
    datasets: [
      {
        data: [input?.new ?? 0, input?.returning ?? 0],
        backgroundColor: ['#5B61F4', '#5AC8FA'],
        borderColor: '#fff',
        borderWidth: 4,
        cutout: '70%',
        hoverOffset: 8,
        hoverBorderColor: '#fff',
      },
    ],
  };
};

const getDeviceDonutData = (input) => {
  const dataView = (input?.data ?? []).length ? input.data : EMPTY_VALUES.deviceType;
  const labels = dataView.map((d) => d.name);
  const data = dataView.map((d) => d.visitors) ?? [];

  return {
    labels,
    datasets: [
      {
        data,
        backgroundColor: ['#DF6061', '#FCC34A', '#2672FA'],
        borderColor: '#fff',
        borderWidth: 4,
        cutout: '70%',
        hoverOffset: 8,
        hoverBorderColor: '#fff',
      },
    ],
  };
};

function getYScaleCallback(metric) {
  if (metric === 'bounceRate') {
    return (value) => {
      const percentage = value * 100;
      return (percentage % 1 === 0 ? percentage.toFixed(0) : percentage.toFixed(2)) + '%';
    };
  }

  if (metric === 'avgSessionDuration') {
    return (value) => {
      let val;
      if (typeof value === 'string') {
        val = Number(value.replaceAll(',', ''));
      } else {
        val = value;
      }
      return msToHumanDuration(val, true, true);
    };
  }
  return (value) => abbreviateNumber(value).toLowerCase();
}

function getYScaleStepSize(metric) {
  if (metric === 'bounceRate') {
    return 0.25;
  }
  if (metric === 'avgSessionDuration') {
    return 1000;
  }
  return 1000;
}

function getMetricName(metric) {
  switch (metric) {
    case 'uniqueVisitors':
      return 'Unique visitors';
    case 'pageViews':
      return 'Pageviews';
    case 'avgSessionDuration':
      return 'Duration';
    case 'sessions':
      return 'Sessions';
    case 'bounceRate':
      return 'Bounce rate';
  }
}

function getSuggestedMax(metric) {
  switch (metric) {
    case 'avgSessionDuration':
      return 60000;
    case 'bounceRate':
      return 1;
    default:
      return 4000;
  }
}

function getMax(metric) {
  switch (metric) {
    case 'bounceRate':
      return 1;
    default:
      return undefined;
  }
}

function generateEmptyDay(date) {
  return { date: format(date, 'yyyy-MM-dd'), value: 0 };
}

function generateEmptyRange([startDate, endDate, predefinedRange]) {
  let parsedStart, parsedEnd;

  if (predefinedRange) {
    const [predefinedStart, predefinedEnd] = parsePredefinedRange(predefinedRange);
    parsedStart = fromUnixTime(predefinedStart);
    parsedEnd = fromUnixTime(predefinedEnd);
  } else {
    if (!startDate || !endDate) {
      return [];
    }
    parsedStart = fromUnixTime(startDate);
    parsedEnd = fromUnixTime(endDate);
  }

  // ensure start is before end
  if (parsedStart > parsedEnd) {
    return [];
  }

  if (isSameDay(parsedEnd, parsedStart)) {
    return [generateEmptyDay(parsedEnd)];
  }

  const range = eachDayOfInterval({ start: parsedStart, end: parsedEnd });
  return range.map(generateEmptyDay);
}
